
import { Component, Vue, Watch } from "vue-property-decorator";
import UIkit from "uikit";
import Icons from "uikit/dist/js/uikit-icons";
import AuthService from "@/services/auth_service";
import HeaderSearch from "@/components/HeaderSearch.vue";
import { Roles } from "@/models/user";

UIkit.use(Icons);

@Component({ components: { HeaderSearch } })
export default class App extends Vue {
  private authService = new AuthService();

  get isLoggedIn(): boolean {
    return this.$store.getters.isLoggedIn;
  }

  /**
   * Everywhere but the grid — which has its own search — and the login screen,
   * where there is no session to query the catalog with.
   */
  get showSearch(): boolean {
    return this.isLoggedIn && this.$route.name !== "fabrics" && this.$route.name !== "login";
  }

  get userLabel(): string {
    const u = this.$store.getters.user;
    return u ? u.email || "Admin" : "";
  }

  async created(): Promise<void> {
    // Hydrate auth state on app load.
    try {
      const user = await this.authService.checkAuth();
      if (user.role === Roles.Admin) {
        await this.$store.dispatch("setAuth", user);
      } else {
        await this.$store.dispatch("clearAuth");
      }
    } catch (e) {
      await this.$store.dispatch("clearAuth");
    }
  }

  async onLogout(): Promise<void> {
    try {
      await this.authService.logout();
    } catch (e) {
      // ignore
    }
    await this.$store.dispatch("clearAuth");
    this.$router.push({ name: "login" }).catch(() => undefined);
  }

  /**
   * Centralized "message that survives a navigation" handler.
   *
   * A toast fired right before $router.push is racy — the firing component is
   * being torn down, and on a hard reload of the destination URL it never fires
   * at all. So the actor encodes the outcome into the destination's query string
   * (?created= / ?deleted=) and this watcher renders it once the destination is
   * live. `immediate: true` is what makes it also fire on a cold page load (F5),
   * not just on an in-app route change.
   *
   * The param is then stripped so a refresh doesn't replay the toast, and so the
   * URL a user copies out of the address bar is clean. Use `replace`, not
   * `push`, or the stripped URL becomes a second history entry and the Back
   * button appears to do nothing.
   */
  @Watch("$route.query", { immediate: true, deep: true })
  protected onQueryChange(): void {
    const q = this.$route.query;
    const created = q.created as string | undefined;
    const deleted = q.deleted as string | undefined;
    if (!created && !deleted) return;

    this.$nextTick(() => {
      if (created) {
        UIkit.notification({
          message: `${decodeURIComponent(created)} created.`,
          status: "success",
        });
      } else if (deleted) {
        UIkit.notification({
          message: `${decodeURIComponent(deleted)} deleted.`,
          status: "danger",
        });
      }
      const rest = { ...this.$route.query };
      delete rest.created;
      delete rest.deleted;
      this.$router.replace({ query: rest }).catch(() => undefined);
    });
  }
}
